import { getEndCursor } from 'components/Blocks/Pagination/paginationHelpers';
import Breadcrumbs from 'components/Layout/Breadcrumbs/Breadcrumbs';
import CommonLayout from 'components/Layout/CommonLayout';
import Webline from 'components/Layout/Webline/Webline';
import ProductDetail from 'components/Pages/ProductDetail/ProductDetail';
import { getFirstImage } from 'connectors/image/Image';
import { mapProductDetailApiData, mapVariantDetailApiData } from 'connectors/products/ProductDetail';
import {
    MainVariantDetailFragmentApi,
    ProductDetailQueryApi,
    ProductDetailQueryDocumentApi,
    ProductDetailQueryVariablesApi,
    ReviewQueryDocumentApi,
    useProductDetailQueryApi,
    VariantDetailFragmentApi,
} from 'graphql/generated';
import { createClient } from 'helpers/createClient';
import { initServerSideProps, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getIsRedirectedFromSsr } from 'helpers/isServer';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { getSeoTitleAndDescriptionForFriendlyUrlPage } from 'helpers/seo/getSeoTitleAndDescriptionForFriendlyUrlPage';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { handleServerSideErrorResponseForFriendlyUrls } from 'helpers/serverSide/handleServerSideErrorResponseForFriendlyUrls';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { useGtmFriendlyPageView } from 'hooks/gtm/useGtmFriendlyPageView';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { NextPageExtended } from 'types/global';
import { REVIEWS_DEFAULT_PAGE_SIZE } from 'types/review';
import { CombinedError, OperationResult } from 'urql';
import { getCatNumFromServerSideUrl, getSlugFromServerSideUrl, getSlugFromUrl } from 'utils/getSlugFromUrl';
import { useGtmPageViewEvent } from 'utils/Gtm/EventFactories';
import { getGtmPageInfoForFriendlyUrl } from 'utils/Gtm/Gtm';

const ProductDetailPage: NextPageExtended<ServerSidePropsType> = () => {
    const router = useRouter();
    const [shouldRefetch, setShouldRefetch] = useState(false);
    const slug = getUrlWithoutGetParameters(router.asPath);
    const catNumFromUrl = getCatNumFromServerSideUrl(router.asPath);
    const currentDomainConfig = useDomainConfig();
    const { defaultStoreUuid } = usePersistStore((s) => s);
    const [{ data: productData, fetching, error }, refetch] = useProductDetailQueryApi({
        variables: {
            urlSlug: catNumFromUrl ? null : getSlugFromUrl(slug),
            defaultStoreUuid,
            catNum: catNumFromUrl,
        },
    });

    useQueryError(error, error instanceof CombinedError);

    const mappedProduct = useMemo(() => {
        if (!productData?.product) {
            return null;
        }

        if (productData.product.__typename === 'RegularProduct') {
            return mapProductDetailApiData(productData.product, currentDomainConfig.currencyCode);
        }

        return mapVariantDetailApiData(
            productData.product as MainVariantDetailFragmentApi | VariantDetailFragmentApi,
            currentDomainConfig.currencyCode,
        );
    }, [currentDomainConfig.currencyCode, productData?.product]);

    const productSeoTitleAndDescription = mappedProduct
        ? getSeoTitleAndDescriptionForFriendlyUrlPage(mappedProduct)
        : undefined;

    const gtmProductUrlPageViewEvent = useGtmPageViewEvent(
        getGtmPageInfoForFriendlyUrl(mappedProduct, slug, mappedProduct?.breadcrumb),
    );
    useGtmFriendlyPageView(gtmProductUrlPageViewEvent, slug, fetching);

    const productMainImage = mappedProduct?.images ? getFirstImage(mappedProduct.images) : undefined;

    useBroadcastChannel({
        name: 'orderCompleteBroadcast',
        callBack: () => {
            setShouldRefetch(true);
        },
    });

    useEffect(() => {
        if (shouldRefetch) {
            refetch({ requestPolicy: 'network-only' });
            setShouldRefetch(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldRefetch]);

    return (
        <CommonLayout
            showLastVisitedProducts
            isFetchingData={fetching}
            title={productSeoTitleAndDescription?.title}
            description={productSeoTitleAndDescription?.description}
            ogImageUrlDefault={productMainImage}
        >
            {mappedProduct?.breadcrumb && (
                <Webline>
                    <Breadcrumbs type="category" key="breadcrumb" breadcrumb={mappedProduct.breadcrumb} />
                </Webline>
            )}
            {mappedProduct && <ProductDetail product={mappedProduct} fetching={fetching} />}
        </CommonLayout>
    );
};

ProductDetailPage.layout = 'plain';

export const getServerSideProps = getServerSidePropsWrapper(
    ({ domainConfig, redisClient, ssrExchange, t }) =>
        async (context) => {
            const client = createClient({
                publicGraphqlEndpoint: domainConfig.publicGraphqlEndpoint,
                ssrExchange,
                t,
                context,
                redisClient,
            });
            const catNumFromUrl = getCatNumFromServerSideUrl(context.req.url ?? '');

            const productResponse: OperationResult<ProductDetailQueryApi, ProductDetailQueryVariablesApi> = await client
                .query(ProductDetailQueryDocumentApi, {
                    urlSlug: catNumFromUrl
                        ? null
                        : getSlugFromServerSideUrl(context.req.url ?? '', context.req.headers),
                    catNum: catNumFromUrl,
                    defaultStoreUuid: context.req.cookies.defaultStoreUuid ?? null,
                })
                .toPromise();

            await client
                .query(ReviewQueryDocumentApi, {
                    productUuid: productResponse.data?.product.uuid,
                    after: getEndCursor(0, 0, REVIEWS_DEFAULT_PAGE_SIZE),
                    first: REVIEWS_DEFAULT_PAGE_SIZE,
                })
                .toPromise();

            const video = context.req.url?.includes('video') ? '#video' : '';

            if (catNumFromUrl && productResponse.data?.product) {
                return {
                    redirect: {
                        destination: '/' + productResponse.data.product.slug + video,
                        statusCode: 301,
                    },
                };
            }

            if (getIsRedirectedFromSsr(context.req.headers)) {
                const serverSideErrorResponse = handleServerSideErrorResponseForFriendlyUrls(
                    productResponse.error?.graphQLErrors,
                    productResponse.data?.product,
                    context.res,
                );

                if (serverSideErrorResponse) {
                    return serverSideErrorResponse;
                }
            }

            const initServerSideData = await initServerSideProps({ context, domainConfig, client, ssrExchange });

            return initServerSideData;
        },
);
export default ProductDetailPage;
